import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gruppeApi } from '../api/gruppe';
import VerfahrenStateBadge from '../components/VerfahrenStateBadge';
import AnfragenStateBadge from '../components/AnfragenStateBadge';
import InseratBlock from '../components/InseratBlock';
import AssignmentWidget from '../components/AssignmentWidget';
import MapLinkButton from '../components/MapLinkButton';
import { useCan } from '../hooks/useCan';
import { useIsModuleEnabled } from '../hooks/useConfigVisibility';

const fieldClass =
  'w-full bg-transparent border border-gray-300 rounded px-2 py-1 text-sm text-gray-900 ' +
  'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors';

export default function GruppeDetail() {
  const { gruppeId } = useParams<{ gruppeId: string }>();
  const id = Number(gruppeId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showInserate = useIsModuleEnabled('inserate');
  const showAnfragen = useIsModuleEnabled('anfragen');
  const showVerfahren = useIsModuleEnabled('verfahren');
  const canRestore = useCan('restore_own');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: gruppe, isLoading, error } = useQuery({
    queryKey: ['gruppe', gruppeId],
    queryFn: () => gruppeApi.getGruppe(id),
    enabled: !!gruppeId,
  });

  const { data: inserate } = useQuery({
    queryKey: ['gruppe', gruppeId, 'inserate'],
    queryFn: () => gruppeApi.getInserate(id),
    enabled: !!gruppeId,
  });

  const { data: anfragenList } = useQuery({
    queryKey: ['gruppe', gruppeId, 'anfragen'],
    queryFn: () => gruppeApi.getAnfragen(id),
    enabled: !!gruppeId && showAnfragen,
  });

  const { data: verfahrenList } = useQuery({
    queryKey: ['gruppe', gruppeId, 'verfahren'],
    queryFn: () => gruppeApi.getVerfahren(id),
    enabled: !!gruppeId && showVerfahren,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; beschreibung?: string }) => gruppeApi.updateGruppe(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gruppe', gruppeId] }),
  });

  const removeMutation = useMutation({
    mutationFn: (listingId: number) => gruppeApi.removeListing(id, listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gruppe', gruppeId] });
      queryClient.invalidateQueries({ queryKey: ['gruppe', gruppeId, 'inserate'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => gruppeApi.deleteGruppe(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gruppen'] });
      navigate('/gruppen');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => gruppeApi.restoreGruppe(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gruppe', gruppeId] }),
  });

  const [name, setName] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (gruppe) {
      setName(gruppe.name);
      setBeschreibung(gruppe.beschreibung ?? '');
    }
  }, [gruppe]);

  const isDirty = !!gruppe && (name !== gruppe.name || beschreibung !== (gruppe.beschreibung ?? ''));

  const handleSave = () => {
    if (!name.trim()) return;
    updateMutation.mutate(
      { name: name.trim(), beschreibung: beschreibung.trim() || undefined },
      {
        onSuccess: () => {
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2000);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !gruppe) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Gruppe konnte nicht geladen werden.</p>
          <button onClick={() => navigate('/gruppen')} className="mt-4 text-blue-600 hover:text-blue-800">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/gruppen')} className="text-blue-600 hover:text-blue-800 text-sm mb-2">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{gruppe.name}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="text-sm text-gray-500">
                  {gruppe.inserate_count} Inserat{gruppe.inserate_count !== 1 ? 'e' : ''}
                </span>
                <MapLinkButton to="/karte" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {gruppe.deleted_at ? (
                <>
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700">
                    Ausgeblendet
                  </span>
                  {canRestore && (
                    <button
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
                    >
                      Wiederherstellen
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {confirmDelete && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900">Gruppe ausblenden?</h3>
            <p className="mt-2 text-sm text-gray-600">
              Die Gruppe „{gruppe.name}" wird ausgeblendet. Die zugeordneten Inserate selbst bleiben unverändert.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Abbrechen
              </button>
              <button
                onClick={() => { deleteMutation.mutate(); setConfirmDelete(false); }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Ausblenden
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Zuständigkeit */}
        <AssignmentWidget
          entityType="inserat"
          entityId={gruppe.id}
          assignment={gruppe.assignment ?? null}
          assignFn={(body) => gruppeApi.changeAssignment(id, body)}
          invalidateKeys={[['gruppe', gruppeId!], ['gruppen']]}
        />

        {/* Stammdaten */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Stammdaten</h3>
          </div>
          <div className="px-6">
            <dl className="divide-y divide-gray-200">
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2">
                  <input value={name} onChange={(e) => setName(e.target.value)} className={fieldClass} />
                </dd>
              </div>
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Beschreibung</dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2">
                  <textarea
                    value={beschreibung}
                    onChange={(e) => setBeschreibung(e.target.value)}
                    placeholder="Keine Beschreibung"
                    rows={2}
                    className={`${fieldClass} resize-none`}
                  />
                </dd>
              </div>
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Erstellt</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {new Date(gruppe.created_at).toLocaleString('de-DE')}
                </dd>
              </div>
            </dl>
          </div>
          <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
            {savedFlash && <span className="text-sm text-green-600">Gespeichert</span>}
            {isDirty && !savedFlash && <span className="text-sm text-gray-500">Ungespeicherte Änderungen</span>}
            <button
              onClick={() => { setName(gruppe.name); setBeschreibung(gruppe.beschreibung ?? ''); }}
              disabled={!isDirty || updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Verwerfen
            </button>
            <button
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Inserate */}
        {showInserate && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                Inserate
                <span className="ml-2 text-sm font-normal text-gray-500">({gruppe.inserate_count})</span>
              </h3>
            </div>
            <div className="px-6 py-4 space-y-3">
              {inserate && inserate.length > 0 ? (
                inserate.map((inserat) => (
                  <div key={inserat.id} className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <InseratBlock inserat={inserat} />
                    </div>
                    <button
                      onClick={() => removeMutation.mutate(inserat.id)}
                      disabled={removeMutation.isPending}
                      title="Aus Gruppe entfernen"
                      className="shrink-0 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                    >
                      Entfernen
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">
                  Noch keine Inserate. Wähle Inserate auf der Karte aus und füge sie dieser Gruppe hinzu.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Anfragen */}
        {showAnfragen && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                Anfragen
                <span className="ml-2 text-sm font-normal text-gray-500">({anfragenList?.length ?? 0})</span>
              </h3>
            </div>
            {anfragenList && anfragenList.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notizen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {anfragenList.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/anfragen/${a.id}`)}>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">
                        {a.adresse_text || `#${a.id}`}
                        {a.zipcode && <span className="text-gray-500 font-normal ml-1">{a.zipcode}</span>}
                        {a.city && <span className="text-gray-500 font-normal ml-1">{a.city}</span>}
                      </td>
                      <td className="px-4 py-3 text-sm"><AnfragenStateBadge status={a.status} small /></td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.notes || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {new Date(a.created_at).toLocaleDateString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-8 text-center text-sm text-gray-500">Keine Anfragen verknüpft.</div>
            )}
          </div>
        )}

        {/* Verfahren */}
        {showVerfahren && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                Verfahren
                <span className="ml-2 text-sm font-normal text-gray-500">({verfahrenList?.length ?? 0})</span>
              </h3>
            </div>
            {verfahrenList && verfahrenList.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktenzeichen</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inserate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {verfahrenList.map((v) => (
                    <tr key={v.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/verfahren/${v.id}`)}>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">{v.aktenzeichen || `Verfahren #${v.id}`}</td>
                      <td className="px-4 py-3 text-sm"><VerfahrenStateBadge staat={v.staat} /></td>
                      <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs">{v.adresse_str || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{v.inserate_count}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {new Date(v.created_at).toLocaleDateString('de-DE')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-8 text-center text-sm text-gray-500">Keine Verfahren verknüpft.</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
